import { createApp } from 'vue';
import { createI18n } from 'vue-i18n';

import axios from 'axios';
// Global Components
import hljsVuePlugin from '@highlightjs/vue-plugin';

import Multiselect from 'vue-multiselect';
import { plugin, defaultConfig } from '@formkit/vue';
import WootWizard from 'components/ui/Wizard.vue';
import FloatingVue from 'floating-vue';
import WootUiKit from 'dashboard/components';
import App from 'dashboard/App.vue';
import i18nMessages from 'dashboard/i18n';
import createAxios from 'dashboard/helper/APIHelper';

import commonHelpers, { isJSONValid } from 'dashboard/helper/commons';
import { sync } from 'vuex-router-sync';
import router, { initalizeRouter } from 'dashboard/routes';
import store from 'dashboard/store';
import constants from 'dashboard/constants/globals';
import * as Sentry from '@sentry/vue';
import {
  initializeAnalyticsEvents,
  initializeChatwootEvents,
} from 'dashboard/helper/scriptHelpers.js';
import FluentIcon from 'shared/components/FluentIcon/DashboardIcon.vue';
import VueDOMPurifyHTML from 'vue-dompurify-html';
import { domPurifyConfig } from 'shared/helpers/HTMLSanitizer.js';
import { initSingleAudioManager } from 'shared/helpers/SingleAudioPlaybackHelper';
import { applyCustomBrandColor } from 'shared/helpers/brandColor';

import { vResizeObserver } from '@vueuse/components';
import { directive as onClickaway } from 'vue3-click-away';

import 'floating-vue/dist/style.css';

// Content scripts de extensões de navegador (não é código nosso — ex.: gerenciadores
// de senha, Meta Pixel Helper) fazem chrome.runtime.sendMessage e, quando o service
// worker da extensão está inativo, a promise rejeita com "Could not establish
// connection. Receiving end does not exist." em TODA página, poluindo o console e o
// Sentry. Silencia SÓ essa rejeição benigna e conhecida (não afeta erros reais).
const BENIGN_EXTENSION_REJECTION =
  'Could not establish connection. Receiving end does not exist';
window.addEventListener('unhandledrejection', event => {
  const message = event?.reason?.message || String(event?.reason ?? '');
  if (message.includes(BENIGN_EXTENSION_REJECTION)) {
    event.preventDefault();
  }
});

applyCustomBrandColor(window?.globalConfig?.BRAND_PRIMARY_COLOR);

// White-label global: troca a marca do produto ("ChatSuite"/"Chatwoot") pela
// marca que o cliente vê (installationName do globalConfig, que recebe o override
// do parceiro em contas white-label). Aplica em TODA string traduzida, então não
// precisa marcar cada texto manualmente. No-op para conta direta (a marca é a
// própria "ChatSuite") e quando a marca ainda não carregou.
const BRAND_LITERALS = /Chatwoot|ChatSuite/g;
const brandPostTranslation = translated => {
  if (typeof translated !== 'string' || !translated.includes('Chat')) {
    return translated;
  }
  let brand;
  try {
    brand = store.getters['globalConfig/get']?.installationName;
  } catch (error) {
    brand = undefined;
  }
  if (!brand) return translated;
  return translated.replace(BRAND_LITERALS, brand);
};

const i18n = createI18n({
  legacy: false, // https://github.com/intlify/vue-i18n/issues/1902
  locale: 'en',
  // Idiomas fora do bundle (mantemos pt_BR/pt/en/es) caem no inglês.
  fallbackLocale: 'en',
  messages: i18nMessages,
  postTranslation: brandPostTranslation,
});
// expose i18n globally for store modules
window.i18n = i18n;

sync(store, router);

const app = createApp(App);
app.use(i18n);
app.use(store);
app.use(router);

// [VITE] Disabled this, need to renable later
if (window.errorLoggingConfig) {
  Sentry.init({
    app,
    dsn: window.errorLoggingConfig,
    denyUrls: [
      // Chrome extensions
      /^chrome:\/\//i,
      /chrome-extension:/i,
      /extensions\//i,

      // Locally saved copies
      /file:\/\//i,

      // Safari extensions.
      /safari-web-extension:/i,
      /safari-extension:/i,
    ],
    integrations: [Sentry.browserTracingIntegration({ router })],
    ignoreErrors: [
      'ResizeObserver loop completed with undelivered notifications',
      // Extensões de navegador (não é nosso código) — ver handler de
      // unhandledrejection acima.
      'Could not establish connection. Receiving end does not exist',
    ],
  });
}

app.use(VueDOMPurifyHTML, domPurifyConfig);
app.use(WootUiKit);
app.use(
  plugin,
  defaultConfig({
    rules: {
      JSON: ({ value }) => isJSONValid(value),
    },
  })
);
app.use(FloatingVue, {
  instantMove: true,
  arrowOverflow: false,
  disposeTimeout: 5000000,
});
app.use(hljsVuePlugin);

app.component('multiselect', Multiselect);
app.component('woot-wizard', WootWizard);
app.component('fluent-icon', FluentIcon);

app.directive('resize', vResizeObserver);
app.directive('on-clickaway', onClickaway);

// load common helpers into js
commonHelpers();
window.WootConstants = constants;
window.axios = createAxios(axios);
// [VITE] Disabled this we don't need it, we can use `useEmitter` directly
// app.prototype.$emitter = emitter;

initializeChatwootEvents();
initializeAnalyticsEvents();
initalizeRouter();
initSingleAudioManager();

window.onload = () => {
  app.mount('#app');
};
